import { Box, Button, Typography, Alert, useTheme } from "@mui/material";
import { Formik, FieldArray } from "formik";
import * as yup from "yup";
import { useSelector }  from "react-redux";
import { useState, useEffect } from "react";
import moment           from "moment";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ScheduleIcon         from "@mui/icons-material/Schedule";

import ShiftDetailForm from "./shiftAssignmentDetail";
import { useShiftsList, useEmployeesList } from "api/hooks/humanResource";
import { requester }   from "api/pesabackend";

const shiftAssignmentSchema = yup.object().shape({
  shifts: yup.array().of(yup.object().shape({
    related_employee:  yup.number().required("required"),
    related_shift:     yup.number().required("required"),
    assignment_period: yup.string().required("required"),
    period_assigned:   yup.string().required("required"),
  })).min(1, "At least one shift must be assigned"),
});

const ShiftAssignmentForm = ({ setOpen, setNewEmployeeShiftData, setIsSuccess }) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const userId             = useSelector((s) => s.authReducer.user.userId);
  const business_id        = useSelector((s) => s.authReducer.business_id);
  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const { shiftList }      = useShiftsList(1, business_branch_id);
  const { employeeList }   = useEmployeesList(1, business_branch_id);

  const [isFailed, setIsFailed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsFailed(false), 3000);
    return () => clearTimeout(t);
  }, [isFailed]);

  const initialValues = {
    shifts: [{ related_employee: "", related_shift: "", assignment_period: "DAY", period_assigned: moment().format("YYYY-MM-DD") }],
  };

  const saveRecord = async (values, onSubmitProps) => {
    const res = await requester.post("/human_resource/employees/assign_shift", JSON.stringify({
      ...values,
      related_business: business_id,
      related_business_branch: business_branch_id,
      added_by: userId,
    }));
    if (res.data.status === 201) { onSubmitProps.resetForm(); setIsSuccess(true); setNewEmployeeShiftData(true); setOpen(false); }
    if (res.data.status === 400) { setIsFailed(true); }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

      {isFailed && <Alert variant="filled" severity="error" sx={{ borderRadius: "8px" }}>Operation was not successful!</Alert>}

      {/* Section header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Box sx={{ width: "34px", height: "34px", borderRadius: "9px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
          <ScheduleIcon sx={{ fontSize: "18px" }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "16px", fontWeight: 400, color: theme.palette.neutral.dark }}>Assign Shifts</Typography>
          <Typography sx={{ fontSize: "12px", color: textSec }}>Select employees and their shifts below</Typography>
        </Box>
      </Box>

      <Formik onSubmit={saveRecord} initialValues={initialValues} validationSchema={shiftAssignmentSchema}>
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, isSubmitting, resetForm }) => (
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

              <Box sx={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px", overflow: "hidden" }}>
                <Box sx={{ px: "1.25rem", py: "0.75rem", borderBottom: `1px solid ${border}`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)" }}>
                  <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>
                    Shift assignments
                  </Typography>
                </Box>

                <Box sx={{ p: "0.75rem 1.25rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  <FieldArray name="shifts" render={(arrayHelpers) => (
                    <>
                      {values.shifts.map((row, index) => (
                        <ShiftDetailForm
                          key={index}
                          index={index}
                          data={row}
                          handleRemoveShiftDetailRow={(i) => arrayHelpers.remove(i)}
                          handleChange={handleChange}
                          errors={arrayHelpers.form.errors}
                          touched={arrayHelpers.form.touched}
                          name={`shifts[${index}]`}
                          handleBlur={handleBlur}
                          shiftList={shiftList}
                          employeeList={employeeList}
                        />
                      ))}

                      <Box
                        onClick={() => arrayHelpers.push({ related_employee: "", related_shift: "", assignment_period: "DAY", period_assigned: moment().format("YYYY-MM-DD") })}
                        sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", p: "0.65rem", borderRadius: "8px", border: `1px dashed rgba(29,158,117,0.4)`, cursor: "pointer", color: accent, fontSize: "13px", fontWeight: 500, transition: "all 0.15s", "&:hover": { backgroundColor: "rgba(29,158,117,0.05)" } }}
                      >
                        <AddCircleOutlineIcon sx={{ fontSize: "15px" }} /> Add another assignment
                      </Box>
                    </>
                  )} />
                </Box>
              </Box>

              <Button
                type="submit"
                disabled={isSubmitting}
                sx={{ fontSize: "13px", fontWeight: 500, px: "1.5rem", py: "0.5rem", borderRadius: "8px", textTransform: "none", backgroundColor: accent, color: "#fff", alignSelf: "flex-start", "&:hover": { backgroundColor: "#0F6E56" } }}
              >
                Save Shift Assignments
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default ShiftAssignmentForm;